import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Lottie from 'lottie-react';
import dataSearching from '../assets/Data_Searching.json';
import { fetchWorkerAttributeEvents } from '../api/workerAttributeEvents';
import { useEnvironment } from '../utils/EnvironmentContext';
import { parseAttributes, extractSkills, buildDescription, skillsDiff, formatTimestamp } from '../helpers/workerAttributeEvents';
import DataTable from './tables/DataTable';
import PaginationBar from './tables/PaginationBar';
import { useFilteredPagination } from '../hooks/useFilteredPagination';
import { buildWorkerAttributeEventsColumns } from '../tables/workerAttributeEventsColumns';
import { DateTime } from 'luxon';
import '../styles/WorkerEventsPage.css';

export default function WorkerAttributeEventsTable({ defaultDate }) {
  const [date, setDate] = useState(defaultDate || DateTime.now().toISODate());
  const [from, setFrom] = useState('00:00');
  const [to, setTo] = useState('23:59');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, completedSlices: 0, totalSlices: 0, totalSoFar: 0 });
  // const [phase, setPhase] = useState('idle'); // unused
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    workerSid: '',
    workerName: '',
    description: '',
    attributes: '',
    eventType: '',
    sid: '',
    // timestamp filter removed; time range slider used instead
  });

  const { env } = useEnvironment();

  const handleFetch = async () => {
    setLoading(true);
    setError('');
    setEvents([]);
    setStreaming(true);
    setPhase('requesting');
    setStatusText('Worker‑attribuutwijzigingen worden aangevraagd…');
    try {
      const { openProgress, fetchFinal } = await fetchWorkerAttributeEvents({ date, from, to, env });
      await new Promise((resolve, reject) => {
        const es = openProgress(
          (meta) => {
            setProgress(p => ({ ...p, totalSlices: meta.totalSlices }));
            setStatusText(`Aangevraagd. Voorbereiden van ${meta.totalSlices} tijdvakken…`);
          },
          (p) => {
            setPhase('retrieving');
            const total = p.totalSlices || progress.totalSlices || 1;
            const completed = Math.min(p.completedSlices || 0, total);
            const slicePercent = Math.floor((completed / total) * 92);
            setProgress({ percent: slicePercent, completedSlices: completed, totalSlices: total, totalSoFar: p.totalSoFar });
            setStatusText(`Ophalen (${completed}/${total})…`);
          },
          async () => {
            try {
              setPhase('processing');
              setStatusText('Verwerken…');
              setProgress(prev => ({ ...prev, percent: Math.max(prev.percent, 95) }));
              const data = await fetchFinal();
              setEvents(data.events || []);
              setPhase('done');
              setStatusText('Klaar');
              setProgress(prev => ({ ...prev, percent: 100 }));
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          (err) => reject(err)
        );
      });
    } catch (e) {
      setError('Ophalen mislukt');
    } finally {
      setLoading(false);
      setStreaming(false);
      setTimeout(() => { setPhase('idle'); setStatusText(''); }, 1200);
    }
  };

  // Preprocess events into "diff rows" (only rows with actual skill changes)
  const diffRows = useMemo(() => {
    if (!events.length) return [];
    const sorted = [...events].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const rows = [];
    for (let i = 0; i < sorted.length; i++) {
      const ev = sorted[i];
      const prevForWorker = [...sorted.slice(0, i)].reverse().find(e => e.workerSid === ev.workerSid);
      const attrs = parseAttributes(ev.attributes);
      const prevAttrs = prevForWorker ? parseAttributes(prevForWorker.attributes) : null;
      const currSkills = extractSkills(attrs);
      const prevSkills = extractSkills(prevAttrs || {});
      // Only include rows where there is a real change compared to a previous snapshot
      if (!prevAttrs) continue; // first snapshot is baseline, don't show
      const { changed } = skillsDiff(prevSkills, currSkills);
      if (!changed) continue; // no change
      const { jsx: descJSX, text: descText } = buildDescription(prevSkills, currSkills);
      rows.push({
        id: i,
        timestamp: ev.timestamp,
        formattedTimestamp: formatTimestamp(ev.timestamp),
        workerName: ev.workerName,
        descriptionNode: descJSX,
        descriptionText: descText
      });
    }
    return rows;
  }, [events]);

  // Compute time bounds from diff rows for the slider (floor to hour for start, ceil to hour for end)
  const timeBounds = useMemo(() => {
    if (!diffRows.length) return null;
    const first = DateTime.fromISO(diffRows[0].timestamp);
    const last = DateTime.fromISO(diffRows[diffRows.length - 1].timestamp);
    const start = first.startOf('hour');
    const end = last.plus({ hours: 1 }).startOf('hour'); // ceil to next hour
    const totalHours = Math.max(1, Math.round(end.diff(start, 'hours').hours));
    return { start, end, totalHours };
  }, [diffRows]);

  // Hour range selection (offsets from timeBounds.start)
  const [hourRange, setHourRange] = useState({ start: 0, end: 0 });
  useEffect(() => {
    if (timeBounds) {
      setHourRange({ start: 0, end: timeBounds.totalHours });
    }
  }, [timeBounds]);

  // Apply filters + time window
  const predicate = useCallback(r => {
    if (filters.workerName && !(r.workerName || '').toLowerCase().includes(filters.workerName.toLowerCase())) return false;
    if (filters.description && !r.descriptionText.toLowerCase().includes(filters.description.toLowerCase())) return false;
    if (timeBounds) {
      const startDT = timeBounds.start.plus({ hours: hourRange.start });
      const endDT = timeBounds.start.plus({ hours: hourRange.end });
      const ts = DateTime.fromISO(r.timestamp);
      if (ts < startDT || ts > endDT) return false;
    }
    return true;
  }, [filters, timeBounds, hourRange]);
  const { pageItems: pageRows, currentPage, totalPages, next: handleNext, prev: handlePrev } = useFilteredPagination(diffRows, 50, predicate);

  const columns = useMemo(() => buildWorkerAttributeEventsColumns(), []);

  // Build a compact worker list and a simple skill snapshot for selected worker
  const allWorkers = useMemo(() => {
    const names = new Map();
    for (const ev of events) {
      const name = ev.workerName || ev.workerSid || 'Onbekend';
      names.set(name, true);
    }
    return Array.from(names.keys()).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const [selectedWorker, setSelectedWorker] = useState(null);
  const selectedSnapshot = useMemo(() => {
    if (!selectedWorker) return null;
    const evs = events.filter(e => (e.workerName || e.workerSid) === selectedWorker);
    if (evs.length === 0) return null;
    const last = evs.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).pop();
    const attrs = parseAttributes(last.attributes);
    const skills = extractSkills(attrs);
    // Build full change history (all diffs within the selected window)
    const sortedEvs = evs.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const changeHistory = [];
    for (let i = 1; i < sortedEvs.length; i++) {
      const prevEv = sortedEvs[i - 1];
      const currEv = sortedEvs[i];
      const prevSkills = extractSkills(parseAttributes(prevEv.attributes));
      const currSkills = extractSkills(parseAttributes(currEv.attributes));
      const { changed, added, removed } = skillsDiff(prevSkills, currSkills);
      if (changed) {
        changeHistory.push({ timestamp: currEv.timestamp, added, removed });
      }
    }

    return { skills, lastTimestamp: last.timestamp, changeHistory };
  }, [selectedWorker, events]);

  return (
    <div>
      <h3>Historiek Skills-wijzigingen</h3>
      <div className="wep-filters">
        <div className="wep-control">
          <label className="wep-label">Datum</label>
          <input className="wep-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="wep-control">
          <label className="wep-label">Van</label>
          <input className="wep-input wep-input--sm" type="time" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="wep-control">
          <label className="wep-label">Tot</label>
          <input className="wep-input wep-input--sm" type="time" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="wep-button" onClick={handleFetch} disabled={loading || !date || !from || !to}>Ophalen</button>
      </div>
      {(loading || streaming) && (
        <div style={{ margin: '0.5rem 0', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
            <Lottie animationData={dataSearching} loop autoplay style={{ width: 160, height: 160, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span>{statusText || 'Events ophalen…'}</span>
                <span>
                  {progress.percent}%
                  {progress.totalSlices > 0 && ` (${progress.completedSlices}/${progress.totalSlices})`}
                </span>
              </div>
              <div style={{ height: 10, background: '#eee', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(progress.percent || 5, 5)}%`, height: '100%', background: '#057fa2', transition: 'width 200ms ease' }} />
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Totaal tot nu toe opgehaald: {progress.totalSoFar}
              </div>
            </div>
          </div>
        </div>
      )}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
              {timeBounds && (() => {
                const total = Math.max(1, timeBounds.totalHours);
                const minGap = total >= 2 ? 1 : 0; // require at least 1 hour gap when possible
                const startPct = Math.max(0, Math.min(100, (hourRange.start / total) * 100));
                const widthPct = Math.max(0, Math.min(100, ((hourRange.end - hourRange.start) / total) * 100));
                return (
                  <div style={{ margin: '6px 0 10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280' }}>
                      <span>{timeBounds.start.plus({ hours: hourRange.start }).toFormat('HH:mm')}</span>
                      <span>tijdvenster</span>
                      <span>{timeBounds.start.plus({ hours: hourRange.end }).toFormat('HH:mm')}</span>
                    </div>
                    <div className="dual-range">
                      <div className="dual-range__track" />
                      <div className="dual-range__range" style={{ left: `${startPct}%`, width: `${widthPct}%` }} />
                      <input
                        className="dual-range__input"
                        type="range"
                        min={0}
                        max={total}
                        step={1}
                        value={hourRange.start}
                        onChange={e => {
                          const raw = Number(e.target.value);
                          const value = Math.min(raw, hourRange.end - minGap);
                          const clamped = Math.max(0, value);
                          setHourRange(prev => ({ ...prev, start: clamped }));
                        }}
                      />
                      <input
                        className="dual-range__input"
                        type="range"
                        min={0}
                        max={total}
                        step={1}
                        value={hourRange.end}
                        onChange={e => {
                          const raw = Number(e.target.value);
                          const value = Math.max(raw, hourRange.start + minGap);
                          const clamped = Math.min(total, value);
                          setHourRange(prev => ({ ...prev, end: clamped }));
                        }}
                      />
                    </div>
                  </div>
                );
              })()}
          {pageRows.length > 0 && (
            <>
              <PaginationBar currentPage={currentPage} totalPages={totalPages} onPrev={handlePrev} onNext={handleNext} />
              <div style={{ maxHeight: '60vh', overflowY: 'auto', borderRadius: 8 }}>
                <DataTable
                  columns={columns}
                  data={pageRows}
                  filters={filters}
                  onFilterChange={setFilters}
                  separateFilters={true}
                />
              </div>
              <PaginationBar currentPage={currentPage} totalPages={totalPages} onPrev={handlePrev} onNext={handleNext} />
            </>
          )}
          {!loading && !error && events.length > 0 && pageRows.length === 0 && (
            <p>Geen rijen met skill‑wijzigingen in dit venster.</p>
          )}
        </div>
        {!loading && events.length > 0 && (
        <div style={{ width: 280, flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Werknemers</div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {allWorkers.length === 0 && <div style={{ color: '#6b7280' }}>Geen werknemers in dit venster</div>}
            {allWorkers.map(name => (
              <button
                key={name}
                onClick={() => setSelectedWorker(name)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  marginBottom: 6,
                  background: selectedWorker === name ? '#eef6f8' : '#fff',
                  color: '#111827',
                  cursor: 'pointer'
                }}
              >
                {name}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            {selectedWorker ? (
              selectedSnapshot ? (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{selectedWorker}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Laatste snapshot: {formatTimestamp(selectedSnapshot.lastTimestamp)}</div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Skills ({selectedSnapshot.skills.length})</div>
                  {selectedSnapshot.skills.length === 0 ? (
                    <div style={{ color: '#6b7280' }}>Geen skills gevonden</div>
                  ) : (
                    <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                      {selectedSnapshot.skills.map((s, idx) => (
                        <li key={idx} style={{ fontSize: 14 }}>{s}</li>
                      ))}
                    </ul>
                  )}
                  {selectedSnapshot.changeHistory && selectedSnapshot.changeHistory.length > 0 ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Alle wijzigingen</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {selectedSnapshot.changeHistory.map((chg, idx) => (
                          <div key={`chg-${idx}`} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8 }}>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{formatTimestamp(chg.timestamp)}</div>
                            {chg.added && chg.added.length > 0 && (
                              <div style={{ marginTop: 2 }}>
                                <div style={{ fontWeight: 600 }}>Toegevoegd</div>
                                <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                                  {chg.added.map((s, i) => (
                                    <li key={`a-${idx}-${i}`}>{s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {chg.removed && chg.removed.length > 0 && (
                              <div style={{ marginTop: 4 }}>
                                <div style={{ fontWeight: 600 }}>Verwijderd</div>
                                <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                                  {chg.removed.map((s, i) => (
                                    <li key={`r-${idx}-${i}`}>{s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, color: '#6b7280' }}>Geen wijzigingen voor skills van deze agent gevonden</div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#6b7280' }}>Geen snapshot beschikbaar</div>
              )
            ) : (
              <div style={{ color: '#6b7280' }}>Selecteer een werknemer om diens skills te bekijken</div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}